/**
 * 与 packages/api/src/routes/image-to-video/lib/config.ts 手工保持一致，
 * 改价、改档位要两边一起改。实际扣费以服务端为准。
 */
/** /image-to-video 页的三个 tab。text 不在这里：那页不暴露文生视频 */
export const I2V_MODES = ["start", "between", "reference"] as const;
/** text = 纯文生视频（0 张图），只在首页 hero 暴露 */
export type I2VMode = (typeof I2V_MODES)[number] | "text";

export const I2V_RESOLUTIONS = ["360p", "480p", "720p", "1080p", "4K"] as const;
export type I2VResolution = (typeof I2V_RESOLUTIONS)[number];

export const I2V_ASPECT_RATIOS = [
	"auto",
	"16:9",
	"9:16",
	"1:1",
	"4:3",
	"3:4",
	"21:9",
	"3:2",
	"2:3",
] as const;
export type I2VAspectRatio = (typeof I2V_ASPECT_RATIOS)[number];

export const I2V_PROMPT_MAX_LENGTH = 1000;

/** long = 能出 15 秒以上的长视频档（Wan 3.0，最长 30 秒） */
export type I2VTier = "basic" | "fast" | "quality" | "master" | "long";

export type I2VPricing =
	| {
			kind: "perSec";
			perSec: Partial<Record<I2VResolution, number>>;
	  }
	| {
			kind: "perClip";
			credits: number;
			creditsByResolution?: Partial<Record<I2VResolution, number>>;
	  }
	/** 按时长档位计价、与分辨率无关（Gemini 的 360p/720p/1080p 同价） */
	| {
			kind: "perClipByDuration";
			credits: Record<number, number>;
			creditsByResolution?: Partial<
				Record<I2VResolution, Record<number, number>>
			>;
	  };

export type I2VModelConfig = {
	label: string;
	tier: I2VTier;
	/** 上游模型标识，也是历史记录里 `model` 字段存的值 */
	providerModel: string;
	modes: readonly I2VMode[];
	/** 只给指定落地页用，不进 getI2VModelsForMode 的公共下拉。同后端 config.ts */
	pageOnly?: boolean;
	resolutions: readonly I2VResolution[];
	/** 每次批量生成可选的输出数量，每条分别提交。 */
	outputOptions: readonly number[];
	/** 允许选择比例所需的最少输入图片数。 */
	aspectRatioMinImages?: number;
	allowAutoReferenceRatio?: boolean;
	/** 首次进页面用哪档，不填走 DEFAULT_I2V_RESOLUTION。切模型时不覆盖用户已选的档 */
	defaultResolution?: I2VResolution;
	aspectRatios?: readonly I2VAspectRatio[];
	aspectRatiosByMode?: Partial<Record<I2VMode, readonly I2VAspectRatio[]>>;
	/** 超出范围的输入图服务端会居中裁到 9:16 / 16:9。同后端 config.ts */
	inputAspectRange?: readonly [number, number];

	maxReferenceImages?: number;
	/** reference 模式收参考视频（2026-10-10 起）。同后端 config.ts，改价两边一起改 */
	referenceVideo?: {
		maxCount: number;
		minSeconds: number;
		/** 所有参考视频合计最长秒数，前端转码时照它截 */
		maxTotalSeconds: number;
		/** 视频秒数 + 生成秒数的上限（Wan 3.0 要求 ≤30） */
		maxCombinedSeconds?: number;
		/** 带视频时的每秒单价，按（视频秒数 + 生成秒数）计；不写沿用 pricing.perSec */
		perSec?: Partial<Record<I2VResolution, number>>;
		/** 计费秒数至少是生成秒数的几倍（Kling 4.0 是 2）。同后端 config.ts */
		minBilledOutputMultiple?: number;
		/** 必须带一段参考视频，照片照常必填（Kling 2.6 motion-control）。同后端 config.ts */
		required?: boolean;
		/** 只按视频秒数计费、不加生成秒数。同后端 config.ts */
		billVideoSecondsOnly?: boolean;
	};
	/** reference 模式收参考音频（不计费）。同后端 config.ts */
	referenceAudio?: {
		maxCount: number;
		minSeconds: number;
		maxTotalSeconds: number;
		/** 上游要求音频必须配参考图或视频 */
		needsVisual?: boolean;
		/** 音频驱动对口型：音频必传，成片和音频一样长、按音频秒数计费。同后端 config.ts */
		drivesDuration?: boolean;
	};
	duration: {
		min: number;
		max: number;
		default: number;
		options?: readonly number[];
		optionsByMode?: Partial<Record<I2VMode, readonly number[]>>;
	};
	pricing: I2VPricing;
};

export const I2V_MODELS = {
	basic: {
		label: "Basic",
		tier: "basic",
		providerModel: "doubao-seedance-1.0-pro-fast",
		modes: ["start", "text"],
		resolutions: ["480p", "720p", "1080p"],
		defaultResolution: "480p",
		inputAspectRange: [0.4, 2.5],
		aspectRatios: ["auto", "16:9", "9:16", "1:1", "4:3", "3:4", "21:9"],
		aspectRatiosByMode: {
			text: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9"],
		},
		duration: { min: 2, max: 12, default: 5 },
		outputOptions: [1, 2, 3, 4],
		pricing: {
			kind: "perSec",
			perSec: { "480p": 1.2, "720p": 2.8, "1080p": 6 },
		},
	},
	// 首尾帧入门档（Seedance 2.0 Mini），同后端 config.ts 的说明。默认 480p：免费档只能用 480p
	"basic-between": {
		label: "Basic",
		tier: "basic",
		providerModel: "bytedance/seedance-2-mini",
		modes: ["between"],
		resolutions: ["480p", "720p"],
		defaultResolution: "480p",
		aspectRatios: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9", "auto"],
		duration: { min: 4, max: 15, default: 5 },
		outputOptions: [1, 2, 3, 4],
		pricing: {
			kind: "perSec",
			perSec: { "480p": 3.8, "720p": 8.2 },
		},
	},
	"fast-2.0": {
		label: "Fast 2.0",
		tier: "fast",
		providerModel: "veo3_lite",
		modes: ["start", "between", "reference", "text"],
		resolutions: ["720p", "1080p", "4K"],
		aspectRatios: ["16:9", "9:16", "auto"],
		aspectRatiosByMode: { text: ["16:9", "9:16"] },
		maxReferenceImages: 3,
		duration: {
			min: 4,
			max: 8,
			default: 8,
			options: [4, 6, 8],
			optionsByMode: { reference: [8] },
		},
		outputOptions: [1, 2, 3, 4],
		pricing: {
			kind: "perClip",
			credits: 15,
			creditsByResolution: { "1080p": 22.5, "4K": 75 },
		},
	},
	// Veo 3.1 另外两档，同后端 config.ts 的说明。下拉里和 Lite 合成一条，见 I2V_MODEL_FAMILIES
	"veo-3.1-fast": {
		label: "Veo 3.1 Fast",
		tier: "fast",
		providerModel: "veo3_fast",
		modes: ["start", "between", "reference", "text"],
		resolutions: ["720p", "1080p", "4K"],
		aspectRatios: ["16:9", "9:16", "auto"],
		aspectRatiosByMode: { text: ["16:9", "9:16"] },
		maxReferenceImages: 3,
		duration: {
			min: 4,
			max: 8,
			default: 8,
			options: [4, 6, 8],
			optionsByMode: { reference: [8] },
		},
		outputOptions: [1, 2, 3, 4],
		pricing: {
			kind: "perClip",
			credits: 30,
			creditsByResolution: { "1080p": 37.5, "4K": 90 },
		},
	},
	"veo-3.1-quality": {
		label: "Veo 3.1 Quality",
		tier: "quality",
		providerModel: "veo3",
		modes: ["start", "between", "text"],
		resolutions: ["720p", "1080p", "4K"],
		aspectRatios: ["16:9", "9:16", "auto"],
		aspectRatiosByMode: { text: ["16:9", "9:16"] },
		duration: { min: 4, max: 8, default: 8, options: [4, 6, 8] },
		outputOptions: [1, 2, 3, 4],
		pricing: {
			kind: "perClip",
			credits: 225,
			creditsByResolution: { "1080p": 232.5, "4K": 285 },
		},
	},
	"fast-3.0": {
		label: "Fast 3.0",
		aspectRatioMinImages: 2,
		allowAutoReferenceRatio: true,
		tier: "fast",
		providerModel: "grok-imagine-video-1-5-preview",
		modes: ["start", "reference", "text"],
		aspectRatios: ["auto", "1:1", "16:9", "9:16", "3:2", "2:3"],
		aspectRatiosByMode: { text: ["16:9", "9:16", "1:1", "3:2", "2:3"] },
		maxReferenceImages: 7,
		resolutions: ["480p", "720p", "1080p"],
		defaultResolution: "480p",
		duration: { min: 1, max: 15, default: 5 },
		outputOptions: [1, 2, 3, 4],
		pricing: {
			kind: "perSec",
			perSec: { "480p": 2.4, "720p": 4.5, "1080p": 8 },
		},
	},
	// MiniMax H3、Wan 3.0 与 Wan 3.0 Prime（2026-10-10），同后端 config.ts 的说明
	"quality-2.0": {
		label: "Quality 2.0",
		tier: "quality",
		providerModel: "minimax-h3/image-to-video",
		modes: ["start", "between"],
		inputAspectRange: [0.4, 2.5],
		resolutions: ["720p", "1080p"],
		duration: { min: 4, max: 15, default: 5 },
		outputOptions: [1, 2, 3, 4],
		pricing: { kind: "perSec", perSec: { "720p": 8, "1080p": 13 } },
	},
	"quality-2.0-ref": {
		label: "Quality 2.0",
		tier: "quality",
		providerModel: "minimax-h3/reference-to-video",
		modes: ["reference"],
		inputAspectRange: [0.4, 2.5],
		resolutions: ["720p", "1080p"],
		aspectRatios: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9", "auto"],
		maxReferenceImages: 5,
		referenceVideo: { maxCount: 3, minSeconds: 2, maxTotalSeconds: 15 },
		referenceAudio: {
			maxCount: 3,
			minSeconds: 2,
			maxTotalSeconds: 15,
			needsVisual: true,
		},
		duration: { min: 4, max: 15, default: 5 },
		outputOptions: [1, 2, 3, 4],
		pricing: { kind: "perSec", perSec: { "720p": 8, "1080p": 13 } },
	},
	"quality-2.0-text": {
		label: "Quality 2.0",
		tier: "quality",
		providerModel: "minimax-h3/text-to-video",
		modes: ["text"],
		resolutions: ["720p", "1080p"],
		aspectRatios: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9"],
		duration: { min: 4, max: 15, default: 5 },
		outputOptions: [1, 2, 3, 4],
		pricing: { kind: "perSec", perSec: { "720p": 8, "1080p": 13 } },
	},
	"wan-3.0": {
		label: "Wan 3.0",
		tier: "long",
		providerModel: "wan/3-0-video",
		modes: ["start", "between", "reference", "text"],
		resolutions: ["480p", "720p", "1080p"],
		defaultResolution: "480p",
		aspectRatios: ["auto", "16:9", "9:16", "1:1", "4:3", "3:4"],
		aspectRatiosByMode: { text: ["16:9", "9:16", "1:1", "4:3", "3:4"] },
		maxReferenceImages: 9,
		referenceVideo: {
			maxCount: 3,
			minSeconds: 1,
			maxTotalSeconds: 15,
			maxCombinedSeconds: 30,
		},
		referenceAudio: { maxCount: 3, minSeconds: 1, maxTotalSeconds: 15 },
		duration: { min: 2, max: 30, default: 5 },
		outputOptions: [1, 2, 3, 4],
		pricing: {
			kind: "perSec",
			perSec: { "480p": 8, "720p": 16, "1080p": 32 },
		},
	},
	"wan-3.0-prime": {
		label: "Wan 3.0 Prime",
		tier: "fast",
		providerModel: "wan/3-0-video-prime",
		modes: ["start", "between", "reference", "text"],
		resolutions: ["480p", "720p", "1080p"],
		defaultResolution: "480p",
		aspectRatios: ["auto", "16:9", "9:16", "1:1", "4:3", "3:4"],
		aspectRatiosByMode: { text: ["16:9", "9:16", "1:1", "4:3", "3:4"] },
		maxReferenceImages: 9,
		referenceVideo: {
			maxCount: 3,
			minSeconds: 1,
			maxTotalSeconds: 15,
			maxCombinedSeconds: 30,
		},
		referenceAudio: { maxCount: 3, minSeconds: 1, maxTotalSeconds: 15 },
		duration: { min: 2, max: 30, default: 5 },
		outputOptions: [1, 2, 3, 4],
		pricing: {
			kind: "perSec",
			perSec: { "480p": 12.2, "720p": 25.2, "1080p": 50.4 },
		},
	},
	"quality-3.0": {
		label: "Quality 3.0",
		tier: "quality",
		providerModel: "google/gemini-omni-flash-1-1",
		modes: ["start", "between", "reference", "text"],
		resolutions: ["360p", "720p", "1080p", "4K"],
		defaultResolution: "1080p",
		aspectRatios: ["16:9", "9:16"],
		maxReferenceImages: 7,
		duration: { min: 4, max: 10, default: 6, options: [4, 6, 8, 10] },
		outputOptions: [1, 2, 3, 4],
		pricing: {
			kind: "perClipByDuration",
			credits: { 4: 45, 6: 60, 8: 75, 10: 90 },
			creditsByResolution: { "4K": { 4: 105, 6: 120, 8: 135, 10: 150 } },
		},
	},
	"master-2.0": {
		label: "Master 2.0",
		tier: "master",
		providerModel: "kling-3.0-omni/image-to-video",
		modes: ["start", "between"],
		resolutions: ["720p", "1080p", "4K"],
		aspectRatios: ["16:9", "9:16", "1:1"],
		aspectRatiosByMode: { between: ["auto"] },
		duration: { min: 3, max: 12, default: 5 },
		outputOptions: [1, 2, 3, 4],
		pricing: {
			kind: "perSec",
			perSec: { "720p": 18, "1080p": 23, "4K": 67 },
		},
	},
	"master-2.0-ref": {
		label: "Master 2.0",
		tier: "master",
		providerModel: "kling-3.0-omni/reference-to-video",
		modes: ["reference"],
		resolutions: ["720p", "1080p", "4K"],
		aspectRatios: ["16:9", "9:16", "1:1"],
		maxReferenceImages: 7,
		duration: { min: 3, max: 12, default: 5 },
		outputOptions: [1, 2, 3, 4],
		pricing: {
			kind: "perSec",
			perSec: { "720p": 18, "1080p": 23, "4K": 67 },
		},
	},
	"master-2.0-text": {
		label: "Master 2.0",
		tier: "master",
		providerModel: "kling-3.0-omni/text-to-video",
		modes: ["text"],
		resolutions: ["720p", "1080p", "4K"],
		aspectRatios: ["16:9", "9:16", "1:1"],
		duration: { min: 3, max: 12, default: 5 },
		outputOptions: [1, 2, 3, 4],
		pricing: {
			kind: "perSec",
			perSec: { "720p": 18, "1080p": 23, "4K": 67 },
		},
	},
	// 同后端 config.ts：keyframe / reference 两个端点按 label 合成一档，始终带音频
	"kling-4.0": {
		label: "Kling 4.0",
		tier: "long",
		providerModel: "kling-4.0/keyframe-to-video",
		modes: ["start", "between", "text"],
		pageOnly: true,
		resolutions: ["720p", "1080p"],
		aspectRatios: ["auto"],
		aspectRatiosByMode: { text: ["16:9", "9:16", "1:1", "21:9"] },
		duration: { min: 3, max: 30, default: 5 },
		outputOptions: [1, 2, 3, 4],
		pricing: { kind: "perSec", perSec: { "720p": 28.5, "1080p": 42.5 } },
	},
	"kling-4.0-ref": {
		label: "Kling 4.0",
		tier: "long",
		providerModel: "kling-4.0/reference-to-video",
		modes: ["reference"],
		pageOnly: true,
		resolutions: ["720p", "1080p"],
		aspectRatios: ["auto", "16:9", "9:16", "1:1", "21:9"],
		maxReferenceImages: 9,
		referenceVideo: {
			maxCount: 3,
			minSeconds: 2,
			maxTotalSeconds: 30,
			perSec: { "720p": 17, "1080p": 25.5 },
			minBilledOutputMultiple: 2,
		},
		duration: { min: 3, max: 30, default: 5 },
		outputOptions: [1, 2, 3, 4],
		pricing: { kind: "perSec", perSec: { "720p": 28.5, "1080p": 42.5 } },
	},
	// /ai-dance-video-generator 的 HD：照片 + 舞蹈视频动作迁移，只按视频秒数计费。同后端 config.ts
	"kling-2.6-motion": {
		label: "Kling 2.6 Motion Control",
		tier: "quality",
		providerModel: "kling-2.6/motion-control",
		modes: ["reference"],
		pageOnly: true,
		resolutions: ["720p"],
		// 上游不收画幅，成片跟照片走；这里只为过 reference tab「必须明确选比例」的校验
		aspectRatios: ["16:9", "9:16", "1:1"],
		inputAspectRange: [0.4, 2.5],
		maxReferenceImages: 1,
		referenceVideo: {
			maxCount: 1,
			minSeconds: 3,
			maxTotalSeconds: 10,
			required: true,
			billVideoSecondsOnly: true,
		},
		duration: { min: 3, max: 10, default: 8 },
		outputOptions: [1, 2, 3, 4],
		pricing: { kind: "perSec", perSec: { "720p": 11 } },
	},
	"master-3.0": {
		label: "Master 3.0",
		tier: "master",
		providerModel: "bytedance/seedance-2",
		modes: ["start", "between", "reference", "text"],
		resolutions: ["480p", "720p", "1080p", "4K"],
		aspectRatios: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9", "auto"],
		aspectRatiosByMode: {
			text: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9"],
		},
		maxReferenceImages: 9,
		duration: { min: 4, max: 15, default: 5 },
		outputOptions: [1, 2, 3, 4],
		pricing: {
			kind: "perSec",
			// 2026-09-14 与 Kie 1:1，和后端 image-to-video/lib/config.ts 保持一致。
			perSec: { "480p": 11.7, "720p": 24.8, "1080p": 102, "4K": 208 },
		},
	},
	// /ai-music-video-generator 专用：一张照片 + 一段歌对口型，同后端 config.ts 的说明
	"omnihuman-2": {
		label: "OmniHuman 2.0",
		tier: "quality",
		providerModel: "omnihuman-2-0",
		modes: ["reference"],
		pageOnly: true,
		resolutions: ["720p"],
		aspectRatios: ["16:9", "9:16", "1:1"],
		inputAspectRange: [0.4, 2.5],
		maxReferenceImages: 1,
		referenceAudio: {
			maxCount: 1,
			minSeconds: 4,
			maxTotalSeconds: 60,
			needsVisual: true,
			drivesDuration: true,
		},
		duration: { min: 4, max: 60, default: 15 },
		outputOptions: [1, 2, 3, 4],
		pricing: { kind: "perSec", perSec: { "720p": 14 } },
	},
	// /seedance-2-ai-video-generator 专用四档，同后端 config.ts 的说明
	"seedance-2-mini": {
		label: "Seedance 2.0 Mini",
		tier: "basic",
		providerModel: "bytedance/seedance-2-mini",
		modes: ["start", "between", "reference", "text"],
		pageOnly: true,
		resolutions: ["480p", "720p"],
		// 首尾帧页默认档，同 basic-between：免费档只能用 480p
		defaultResolution: "480p",
		aspectRatios: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9", "auto"],
		aspectRatiosByMode: {
			text: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9"],
		},
		maxReferenceImages: 9,
		duration: { min: 4, max: 15, default: 5 },
		outputOptions: [1, 2, 3, 4],
		referenceVideo: {
			maxCount: 3,
			minSeconds: 2,
			maxTotalSeconds: 15,
			perSec: { "480p": 2.4, "720p": 5 },
		},
		referenceAudio: {
			maxCount: 3,
			minSeconds: 2,
			maxTotalSeconds: 15,
			needsVisual: true,
		},
		pricing: { kind: "perSec", perSec: { "480p": 3.8, "720p": 8.2 } },
	},
	"seedance-2-fast": {
		label: "Seedance 2.0 Fast",
		tier: "fast",
		providerModel: "bytedance/seedance-2-fast",
		modes: ["start", "between", "reference", "text"],
		pageOnly: true,
		resolutions: ["480p", "720p"],
		aspectRatios: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9", "auto"],
		aspectRatiosByMode: {
			text: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9"],
		},
		maxReferenceImages: 9,
		duration: { min: 4, max: 15, default: 5 },
		outputOptions: [1, 2, 3, 4],
		referenceVideo: {
			maxCount: 3,
			minSeconds: 2,
			maxTotalSeconds: 15,
			perSec: { "480p": 6.8, "720p": 15 },
		},
		referenceAudio: {
			maxCount: 3,
			minSeconds: 2,
			maxTotalSeconds: 15,
			needsVisual: true,
		},
		pricing: { kind: "perSec", perSec: { "480p": 11.7, "720p": 24.8 } },
	},
	"seedance-2": {
		label: "Seedance 2.0",
		tier: "master",
		providerModel: "bytedance/seedance-2",
		modes: ["start", "between", "reference", "text"],
		pageOnly: true,
		resolutions: ["480p", "720p", "1080p", "4K"],
		aspectRatios: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9", "auto"],
		aspectRatiosByMode: {
			text: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9"],
		},
		maxReferenceImages: 9,
		duration: { min: 4, max: 15, default: 5 },
		outputOptions: [1, 2, 3, 4],
		referenceVideo: {
			maxCount: 3,
			minSeconds: 2,
			maxTotalSeconds: 15,
			perSec: { "480p": 11.5, "720p": 25, "1080p": 62, "4K": 128 },
		},
		referenceAudio: {
			maxCount: 3,
			minSeconds: 2,
			maxTotalSeconds: 15,
			needsVisual: true,
		},
		pricing: {
			kind: "perSec",
			perSec: { "480p": 19, "720p": 41, "1080p": 102, "4K": 208 },
		},
	},
	"seedance-2.5": {
		label: "Seedance 2.5",
		tier: "master",
		providerModel: "bytedance/seedance-2-5",
		modes: ["start", "between", "reference", "text"],
		pageOnly: true,
		resolutions: ["480p", "720p", "1080p"],
		aspectRatios: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9", "auto"],
		aspectRatiosByMode: {
			text: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9"],
			// 同后端 config.ts：首帧 / 首尾帧上游只收 adaptive
			start: ["auto"],
			between: ["auto"],
		},
		// kie 允许 30 张参考图，先和其他三档统一 9 张（2026-10-04）
		maxReferenceImages: 9,
		// 2026-10-04 放开到 30s，同后端 config.ts
		duration: { min: 4, max: 30, default: 5 },
		outputOptions: [1, 2, 3, 4],
		referenceVideo: {
			maxCount: 3,
			minSeconds: 2,
			maxTotalSeconds: 30,
			perSec: { "480p": 17, "720p": 38, "1080p": 95 },
		},
		referenceAudio: {
			maxCount: 3,
			minSeconds: 2,
			maxTotalSeconds: 30,
			needsVisual: true,
		},
		pricing: {
			kind: "perSec",
			perSec: { "480p": 28, "720p": 63, "1080p": 158 },
		},
	},
} as const satisfies Record<string, I2VModelConfig>;

export type I2VModelId = keyof typeof I2V_MODELS;

export const I2V_MODEL_IDS = Object.keys(I2V_MODELS) as I2VModelId[];

/** `as const` 收窄掉了可选字段，统一走这个访问器拿回接口类型 */
const I2V_MODELS_BY_ID: Record<I2VModelId, I2VModelConfig> = I2V_MODELS;

export function getI2VModel(model: I2VModelId): I2VModelConfig {
	return I2V_MODELS_BY_ID[model];
}

export function getI2VAspectRatios(
	model: I2VModelId,
	mode: I2VMode,
): readonly I2VAspectRatio[] {
	const config = getI2VModel(model);
	const ratios: readonly I2VAspectRatio[] = config.aspectRatiosByMode?.[
		mode
	] ??
		config.aspectRatios ?? ["auto"];
	// 参考图可以有不同画幅，界面要求用户明确选择输出比例。
	return mode === "reference" && !config.allowAutoReferenceRatio
		? ratios.filter((ratio) => ratio !== "auto")
		: ratios;
}

export function isI2VModelId(value: string): value is I2VModelId {
	return value in I2V_MODELS;
}

/**
 * 一个模型分几档时，下拉里只出一条族名，选中后旁边多一个档位胶囊（2026-10-10
 * 老公定，照 imgveo 的 Veo 3.1；Wan 3.0 的标准 / Prime 同样处理）。variants 顺序即胶囊顺序，第一档是从下拉选中时的默认档
 */
export type I2VModelFamily = {
	name: string;
	variants: readonly { id: I2VModelId; name: string }[];
};

export const I2V_MODEL_FAMILIES: readonly I2VModelFamily[] = [
	{
		name: "Veo 3.1",
		variants: [
			{ id: "fast-2.0", name: "Lite" },
			{ id: "veo-3.1-fast", name: "Fast" },
			{ id: "veo-3.1-quality", name: "Quality" },
		],
	},
	{
		name: "Wan 3.0",
		variants: [
			{ id: "wan-3.0", name: "Standard" },
			{ id: "wan-3.0-prime", name: "Prime" },
		],
	},
];

export function getI2VModelFamily(
	model: I2VModelId,
): I2VModelFamily | undefined {
	return I2V_MODEL_FAMILIES.find((family) =>
		family.variants.some((variant) => variant.id === model),
	);
}

/** 下拉要列的模型：同一族只留名单里排最前的那档代表整族 */
export function collapseI2VModelFamilies(
	models: readonly I2VModelId[],
): I2VModelId[] {
	return models.filter((id) => {
		const family = getI2VModelFamily(id);
		return (
			!family ||
			family.variants.find((variant) => models.includes(variant.id))
				?.id === id
		);
	});
}

/**
 * 已下线、但还留在历史行 metadata.modelId 里的 id。
 * fast-3.0-ref：2026-10-05/06 Grok 换到 fal 期间拆出来的多图 reference 档（走 kie），
 * 换回 kie 后并回 fast-3.0。不放回 I2V_MODELS，免得出现在下拉里。
 */
const LEGACY_I2V_MODEL_IDS: Record<string, I2VModelId> = {
	"fast-3.0-ref": "fast-3.0",
};

/** 历史行的 metadata.modelId → 现在的模型；认不出返回 undefined */
export function resolveI2VHistoryModelId(
	value: string | undefined,
): I2VModelId | undefined {
	if (!value) {
		return undefined;
	}
	return isI2VModelId(value) ? value : LEGACY_I2V_MODEL_IDS[value];
}

/** 某个 tab 能选的模型，顺序即下拉顺序（便宜的在前） */
export function getI2VModelsForMode(mode: I2VMode): I2VModelId[] {
	return I2V_MODEL_IDS.filter((id) => {
		const config = getI2VModel(id);
		return !config.pageOnly && config.modes.includes(mode);
	});
}

/**
 * 每个 tab 的默认模型，取该 tab 里最便宜的一档：start / text 是 Basic（2026-09-30
 * 老公定，首页和 /image-to-video 都默认它），between 是 Seedance 2.0 Mini 入门档
 * （2026-10-02），reference 取 kie 最便宜档。
 */
export const DEFAULT_I2V_MODEL: Record<I2VMode, I2VModelId> = {
	start: "basic",
	between: "basic-between",
	reference: "fast-3.0",
	text: "basic",
};

/**
 * 切 tab 时把已选模型带过去。同一个档位在不同 mode 下可能是两个 id——Master 2.0
 * 的 start/between 走 `master-2.0`、reference 走 `master-2.0-ref`——所以先按
 * label 找回同档位里支持目标 mode 的那个，整个档位都不支持才回落到 tab 默认。
 */
export function carryI2VModelToMode(
	model: I2VModelId,
	mode: I2VMode,
): I2VModelId {
	if (getI2VModel(model).modes.includes(mode)) {
		return model;
	}
	const { label } = getI2VModel(model);
	const sameTier = I2V_MODEL_IDS.find(
		(id) =>
			getI2VModel(id).label === label &&
			getI2VModel(id).modes.includes(mode),
	);
	// 多档族里这档不支持就换同族别的档（Veo 3.1 Quality 没有参考图，切过去落到 Lite）
	const sameFamily = getI2VModelFamily(model)?.variants.find((variant) =>
		getI2VModel(variant.id).modes.includes(mode),
	)?.id;
	return sameTier ?? sameFamily ?? DEFAULT_I2V_MODEL[mode];
}

export const DEFAULT_I2V_RESOLUTION: I2VResolution = "720p";
// 图生视频默认跟输入图画幅走（auto 不传 aspect_ratio 给上游），竖图不再被裁成横版；
// 文生视频没有 auto 可选，clamp 会落到 16:9。与 packages/api 的同名常量保持一致
export const DEFAULT_I2V_ASPECT_RATIO: I2VAspectRatio = "auto";

/** 每个 tab 收几张图 */
export function getMaxI2VImages(
	model: I2VModelId,
	mode: I2VMode,
	resolution?: I2VResolution,
): number {
	if (mode === "text") {
		return 0;
	}
	if (model === "fast-3.0" && resolution === "1080p") {
		return 1;
	}
	if (mode === "start") {
		return 1;
	}
	if (mode === "between") {
		return 2;
	}
	return getI2VModel(model).maxReferenceImages ?? 1;
}

/** 这档在这个 tab 收不收参考视频 */
export function supportsI2VReferenceVideo(
	model: I2VModelId,
	mode: I2VMode,
): boolean {
	return mode === "reference" && !!getI2VModel(model).referenceVideo;
}

/** 这档在这个 tab 收不收参考音频 */
export function supportsI2VReferenceAudio(
	model: I2VModelId,
	mode: I2VMode,
): boolean {
	return mode === "reference" && !!getI2VModel(model).referenceAudio;
}

/** 参考视频的计费秒数：向上取整，留 0.05 秒容差。同后端 config.ts */
export function getBillableVideoSeconds(durationSec: number): number {
	return Math.max(1, Math.ceil(durationSec - 0.05));
}

/** videoSeconds 是参考视频合计的计费秒数；带视频时按（视频秒数 + 生成秒数）× 带视频单价 */
export function getI2VCreditCost(
	model: I2VModelId,
	resolution: I2VResolution,
	duration: number,
	videoSeconds = 0,
): number {
	const config = getI2VModel(model);
	if (
		videoSeconds > 0 &&
		config.referenceVideo &&
		config.pricing.kind === "perSec"
	) {
		const perSec =
			config.referenceVideo.perSec?.[resolution] ??
			config.pricing.perSec[resolution];
		const billedSeconds = config.referenceVideo.billVideoSecondsOnly
			? videoSeconds
			: Math.max(
					duration + videoSeconds,
					duration *
						(config.referenceVideo.minBilledOutputMultiple ?? 1),
				);
		return perSec === undefined ? 0 : Math.round(perSec * billedSeconds);
	}
	const pricing = config.pricing;
	if (pricing.kind === "perClip") {
		return Math.round(
			pricing.creditsByResolution?.[resolution] ?? pricing.credits,
		);
	}
	if (pricing.kind === "perClipByDuration") {
		return (
			(pricing.creditsByResolution?.[resolution] ?? pricing.credits)[
				duration
			] ?? 0
		);
	}
	const perSec = pricing.perSec[resolution];
	if (perSec === undefined) {
		return 0;
	}
	return Math.round(perSec * duration);
}

/** 菜单按最低分辨率、5s 展示；Quality 3.0 用 4s，按条计价的 Veo 3.1 三档用 8s。 */
export function getI2VModelPriceHint(
	model: I2VModelId,
	/** 已加的参考视频计费秒数，收视频的档会按（视频秒数 + 生成秒数）报价 */
	videoSeconds = 0,
): {
	credits: number;
	resolution: I2VResolution;
	duration: number;
} {
	const config = getI2VModel(model);
	const duration =
		config.pricing.kind === "perClip" ? 8 : model === "quality-3.0" ? 4 : 5;
	const resolution = config.resolutions[0];
	return {
		credits: getI2VCreditCost(model, resolution, duration, videoSeconds),
		resolution,
		duration,
	};
}

/** 参数夹回目标模型支持的范围。切 tab / 切模型 / 回填历史都要用 */
export function clampI2VResolution(
	model: I2VModelId,
	resolution: I2VResolution,
): I2VResolution {
	const resolutions = getI2VModel(model).resolutions;
	return resolutions.includes(resolution) ? resolution : resolutions[0];
}

/** 首次进页面时该选哪档分辨率。只用于初始状态，切模型走 clampI2VResolution */
export function getI2VDefaultResolution(model: I2VModelId): I2VResolution {
	return (
		getI2VModel(model).defaultResolution ??
		clampI2VResolution(model, DEFAULT_I2V_RESOLUTION)
	);
}

export function clampI2VDuration(
	model: I2VModelId,
	duration: number,
	mode: I2VMode,
): number {
	const config = getI2VModel(model).duration;
	const options = getI2VDurationOptions(model, mode);
	if (options) {
		return options.includes(duration) ? duration : config.default;
	}
	return Math.min(Math.max(duration, config.min), config.max);
}

export function clampI2VAspectRatio(
	model: I2VModelId,
	ratio: I2VAspectRatio,
	mode: I2VMode = "reference",
): I2VAspectRatio {
	const ratios = getI2VAspectRatios(model, mode);
	if (!ratios?.length) {
		return DEFAULT_I2V_ASPECT_RATIO;
	}
	return ratios.includes(ratio) ? ratio : ratios[0];
}

/** 历史行 metadata 里存的形状，与服务端 I2VRowMetadata 对应 */
export interface I2VRowMetadata {
	tool?: string;
	mode?: I2VMode;
	modelId?: I2VModelId;
	aspectRatio?: I2VAspectRatio;
	generateAudio?: boolean;
	referenceVideoUrls?: string[];
	referenceVideoSeconds?: number;
	referenceAudioUrls?: string[];
	width?: number;
	height?: number;
	fps?: number;
	duration?: number;
}

export const I2V_TOOL_TAG = "image-to-video";

/** 这行历史记录是不是本工具生成的 */
export function isI2VHistoryRow(metadata: unknown): boolean {
	return (metadata as I2VRowMetadata | null)?.tool === I2V_TOOL_TAG;
}

/**
 * 老 /ai-video-generator 工作台（2026-10-06 下线）写的行：没有 tool 标记，
 * model 列是 kie / fal 的几种 Grok 写法，但那条线只出过 Grok，回填一律落到 Grok Imagine 1.5
 */
export const LEGACY_AI_VIDEO_MODEL: I2VModelId = "fast-3.0";

/** 回填表单时用哪个模型：i2v 行看 metadata.modelId，老 ai-video 行认成 Grok；认不出返回 undefined */
export function resolveHistoryRowModelId(
	metadata: unknown,
): I2VModelId | undefined {
	const modelId = resolveI2VHistoryModelId(
		(metadata as I2VRowMetadata | null)?.modelId,
	);
	if (modelId) {
		return modelId;
	}
	return isI2VHistoryRow(metadata) ? undefined : LEGACY_AI_VIDEO_MODEL;
}

/** 回填表单用哪个 mode：老 ai-video 行没存 mode，那条线一次能传 1–7 张，多图的落到 reference 才不丢图 */
export function resolveHistoryRowMode(
	metadata: unknown,
	imageCount: number,
): I2VMode | undefined {
	const mode = (metadata as I2VRowMetadata | null)?.mode;
	if (mode || isI2VHistoryRow(metadata)) {
		return mode;
	}
	return imageCount > 1 ? "reference" : undefined;
}

/** Kling 显式画幅走单镜头 multi_prompt，其提示词上限为 512 字符。 */
export function getI2VPromptMaxLength(
	model: I2VModelId,
	mode: I2VMode,
): number {
	// 上游 OmniHuman 2.0 只收 300 字
	if (model === "omnihuman-2") {
		return 300;
	}
	if (model === "master-2.0-ref") {
		return 3072;
	}
	if (model === "master-2.0") {
		return mode === "start" ? 512 : 3072;
	}
	// 上游 Wan 3.0 收 2 万字、Grok 1.5（kie / fal 兜底）收 4096 字，Waffo 文本审核收 1 万字；
	// 丧尸热梗页的 Wan 15 秒分镜模板有 4.3K 字（2026-10-11 放宽）
	if (model === "wan-3.0" || model === "wan-3.0-prime") {
		return 5000;
	}
	// 上游 Seedance 2.5 收 20480 字；Rumpelstiltskin 的 Pro 要按顺序写满 11 个镜头（2026-10-27 放宽）
	if (model === "fast-3.0" || model === "seedance-2.5") {
		return 4000;
	}
	// 上游 Seedance 2 / Mini / Fast 收 2 万字；Flying Car 坡道换车 prompt 990 字，还要留出备注（2026-10-27 放宽）；
	// Hop Hop Hop HD 用 Seedance 2 完整版跑 3.8K 字分镜（2026-11-01 放宽）
	if (
		model === "seedance-2-mini" ||
		model === "seedance-2-fast" ||
		model === "seedance-2"
	) {
		return 4000;
	}
	// 上游 Kling 4.0 收 8000 字，同后端 config.ts
	if (model === "kling-4.0" || model === "kling-4.0-ref") {
		return 4000;
	}
	if (model === "kling-2.6-motion") {
		return 2500;
	}
	return I2V_PROMPT_MAX_LENGTH;
}

export function getI2VDurationOptions(
	model: I2VModelId,
	mode: I2VMode,
): readonly number[] | undefined {
	const { duration } = getI2VModel(model);
	return duration.optionsByMode?.[mode] ?? duration.options;
}
